var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { AwsUi } from '../../runtime/src';
import { Component, createElement } from 'react';
// eslint-disable-next-line @typescript-eslint/camelcase
import { createPortal, unmountComponentAtNode, unstable_renderSubtreeIntoContainer } from 'react-dom';
import RegionsRegistry from './RegionsRegistry';
import EventsRegistry from './EventsRegistry';
import reactRenderHooksFactory from './react-render-hooks';
import { getEventName, shallowEquals } from './utils';
import logger from '../../utils/src/logger';
import attachMetadataToNode from '../../utils/src/attach-metadata-to-node';
import { toCamelCase } from '../../utils/src/to-camel-case';
function findDefaultRegion(definition) {
    for (var regionName in definition.regions) {
        if (definition.regions[regionName].isDefault) {
            return regionName;
        }
    }
    return;
}
export function createClass(ComponentPrototype) {
    if (!ComponentPrototype.awsUi || !ComponentPrototype.componentDefinition) {
        throw new Error('Provided value is not a valid component');
    }
    var name = ComponentPrototype.awsUi.name;
    var definition = ComponentPrototype.componentDefinition;
    var reactName = toCamelCase(name).replace(/^Awsui/, '');
    var defaultRegionName = findDefaultRegion(definition);
    var AwsUiComponent = /** @class */ (function (_super) {
        __extends(AwsUiComponent, _super);
        function AwsUiComponent() {
            var _this = _super !== null && _super.apply(this, arguments) || this;
            _this.state = {};
            _this.subElements = {};
            _this.component = null;
            _this.ref = null;
            _this.reactBatchUpdate = function (updater) {
                // setState calls take too long to perform. We aggregate the state here to have only one call to React
                // fix for https://issues.amazon.com/AWSUI-5074
                if (!_this.insideBatch) {
                    _this.pendingState = {};
                }
                _this.insideBatch++;
                updater();
                _this.insideBatch--;
                if (!_this.insideBatch) {
                    _this.setState(_this.pendingState);
                    _this.pendingState = null;
                }
            };
            _this.reactMount = function (lifecycleKey, reactContents, element) {
                if (createPortal) {
                    _this.setPortalContent(lifecycleKey, reactContents, element);
                }
                else {
                    unstable_renderSubtreeIntoContainer(_this, reactContents, element);
                }
            };
            _this.reactUnmount = function (lifecycleKey, element) {
                if (createPortal) {
                    _this.setPortalContent(lifecycleKey, null, null);
                }
                else {
                    unmountComponentAtNode(element);
                }
            };
            _this.regionsRegistry = new RegionsRegistry(_this, defaultRegionName, _this.reactMount, _this.reactUnmount);
            _this.eventsRegistry = new EventsRegistry();
            _this.pendingState = {};
            _this.insideBatch = 0;
            return _this;
        }
        AwsUiComponent.prototype.getWidget = function () {
            return this.ref;
        };
        AwsUiComponent.prototype.setPortalContent = function (lifecycleKey, reactContents, element) {
            var stateKey = "subtree-" + lifecycleKey;
            this.subElements[stateKey] = element;
            if (this.state[stateKey] !== reactContents ||
                (stateKey in this.pendingState && this.pendingState[stateKey] !== reactContents)) {
                this.pendingState[stateKey] = reactContents;
            }
        };
        AwsUiComponent.prototype.getComponent = function () {
            return this.component;
        };
        AwsUiComponent.prototype.componentDidMount = function () {
            if (!this.ref) {
                // needed to support test rendering
                // https://issues.amazon.com/issues/AWSUI-7759
                return;
            }
            AwsUi.activate(this.ref, ComponentPrototype, {
                __getWrapperRenderHooks: reactRenderHooksFactory(this.reactMount, this.reactUnmount),
                // use batchedUpdates to prevent React to update underlying regions while the main component update
                // is in progress
                __callBatchUpdate: this.reactBatchUpdate
            });
            this.component = this.ref.component;
            attachMetadataToNode(this.ref);
            this.componentDidUpdate({});
            this.component.hasBeenAttached();
        };
        AwsUiComponent.prototype.componentWillUnmount = function () {
            var _this = this;
            this.reactBatchUpdate(function () {
                _this.component.hasBeenDetached();
                _this.regionsRegistry.unmountAllRegions();
            });
        };
        AwsUiComponent.prototype.componentDidUpdate = function (oldProps) {
            var _this = this;
            if (shallowEquals(oldProps, this.props)) {
                return;
            }
            this.component.__batchUpdate(function () {
                // unset values for all unset properties/attributes
                for (var prop in oldProps) {
                    if (prop in _this.props) {
                        continue;
                    }
                    // Convert special 'children' property to name of default region
                    if (prop === 'children') {
                        prop = defaultRegionName;
                    }
                    var eventName = getEventName(prop);
                    if (eventName in (definition.events || {})) {
                        _this.eventsRegistry.updateHandler(_this.ref, eventName, null);
                    }
                    else if (prop in (definition.regions || {})) {
                        _this.regionsRegistry.updateRegion(prop, null);
                    }
                    else if (prop in (definition.properties || {})) {
                        _this.component.__setToDefault(prop);
                    }
                }
                Object.keys(_this.props).forEach(function (prop) {
                    var value = _this.props[prop];
                    // Convert special 'children' property to name of default region
                    if (prop === 'children') {
                        _this.regionsRegistry.updateDefaultRegion(value);
                    }
                    else {
                        var eventName = getEventName(prop);
                        if (eventName in (definition.events || {})) {
                            _this.eventsRegistry.updateHandler(_this.ref, eventName, value);
                        }
                        else if (prop in (definition.regions || {})) {
                            if (prop === defaultRegionName) {
                                logger.warnOnce("Region alias \"" + prop + "\" for \"" + reactName + "\" has been deprecated. Use \"children\" prop instead");
                            }
                            _this.regionsRegistry.updateRegion(prop, value);
                        }
                        else if (prop in (definition.properties || {})) {
                            _this.component[prop] = _this.props[prop];
                        }
                    }
                });
            });
        };
        AwsUiComponent.prototype.render = function () {
            var _this = this;
            var reactAttributes = {
                id: this.props.id,
                class: this.props.className,
                ref: function (ref) { return (_this.ref = ref); },
                children: Object.keys(this.state)
                    .filter(function (key) { return key.indexOf('subtree-') === 0 && _this.subElements[key]; })
                    .map(function (key) { return createPortal(_this.state[key], _this.subElements[key], key); })
            };
            for (var prop in this.props) {
                if (prop.match(/^data-/)) {
                    reactAttributes[prop] = this.props[prop];
                }
            }
            return createElement(name, reactAttributes);
        };
        AwsUiComponent.displayName = reactName;
        return AwsUiComponent;
    }(Component));
    // Add functions to the prototype
    Object.keys(definition.functions || {}).forEach(function (functionName) {
        AwsUiComponent.prototype[functionName] = function () {
            return this.component[functionName].apply(this.component, arguments);
        };
    });
    return AwsUiComponent;
}
//# sourceMappingURL=create-class.js.map